import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { ModalController } from '@ionic/angular/standalone';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { safeModalDismiss } from 'src/app/core/services/modal/modal.utils';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { SoloQuoteService } from 'src/app/core/services/tiers/solo-quote.service';
import { SoloTiersService } from 'src/app/core/services/tiers/solo-tiers.service';
import { SoloPayOutcome } from 'src/app/features/payments/services/solo-sheet-payment';
import { ThemePipe } from '../../../core/pipes/theme.pipe';
import { ButtonComponent } from '../../../ui/buttons/button/button.component';
import { CurrencyButtonComponent } from '../../../ui/buttons/currency-button/currency-button.component';
import { NavButtonComponent } from '../../../ui/buttons/nav-button/nav-button.component';
import { IconComponent } from '../../../ui/icon/icon/icon.component';
import { SoloStatComponent } from '../components/solo-stat/solo-stat.component';
import { SoloAmountDisplay } from '../utils/solo-tiers.utils';

// Why the drawer's stake can no longer be paid for: the tier list stopped offering it, wager
// refused it, or no target could be fetched.
export type SoloEntryUnavailableReason = 'stake-dropped' | 'tier-refused' | 'quote-failed';

/**
 * Opened over the Solo sheet when wager refused Play before charging anything. For a moved target
 * it shows the target wager offers now, read live from SoloQuoteService: a refetch under it updates
 * the number, and Pay waits for it as the sheet's Play does. Pay charges the stake the refused Play
 * was for, never the sheet's current one (SoloOperatorPayment explains why); another moved target
 * keeps this open on the newer one, and any other end closes the sheet, which closes this too.
 *
 * Once that stake can't be paid for (see SoloEntryUnavailableReason) the drawer says so instead,
 * with an Ok in place of Pay, and stays that way. A refusal for a withdrawn stake opens it that way.
 * Close and Ok both close this drawer, and the sheet closes behind it.
 *
 * On Lightning (`paid`) the stake is already paid for: Continue enters it at the new target, and
 * closing the drawer means it is refunded. A stake the tier list drops is not "no longer
 * available" here, because nothing more is charged and wager decides whether the paid deposit
 * still enters; a refusal from wager closes the sheet with its own alert.
 */
@Component({
  selector: 'app-solo-new-target-drawer',
  templateUrl: './solo-new-target-drawer.component.html',
  styleUrls: ['./solo-new-target-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    TranslocoDirective,
    ThemePipe,
    ButtonComponent,
    CurrencyButtonComponent,
    NavButtonComponent,
    IconComponent,
    SoloStatComponent,
  ],
})
export class SoloNewTargetDrawerComponent {
  // The stake the refused Play was for, and the only one Pay charges.
  readonly tierId = input.required<string>();
  // That stake's entry fee, shown on Pay.
  readonly entry = input<SoloAmountDisplay>();
  readonly hasSecondaryCurrency = input(false);
  // Charges that stake at the live quote.
  readonly pay = input.required<() => Promise<SoloPayOutcome>>();
  // Set when wager refused the stake itself: the drawer opens on the unavailable message.
  readonly tierRefused = input(false);
  // Set for a Lightning stake whose invoice is already paid: `pay` continues its entry.
  readonly paid = input(false);

  private readonly soloQuoteService = inject(SoloQuoteService);
  private readonly soloTiersService = inject(SoloTiersService);
  private readonly themeService = inject(ThemeService);
  private readonly modalController = inject(ModalController);
  private readonly analyticsService = inject(AnalyticsService);

  // Also set by a Pay that wager refuses for the stake.
  private readonly refused = linkedSignal(() => this.tierRefused());

  protected readonly drawerConfig = this.themeService.payDrawerConfig();
  // Must match the Solo sheet's glow choice, so the two read as one surface.
  protected readonly glowClass =
    this.drawerConfig.gradient === 'circle' ? 'glow-circle' : 'glow-linear';

  readonly busy = signal(false);

  readonly target = computed(() => {
    const quote = this.soloQuoteService.quote();
    return quote ? this.themeService.formatNumber(quote.targetScore) : '';
  });
  readonly payDisabled = computed(() => this.busy() || !this.soloQuoteService.ready());

  // Not while a Pay is out: wager's answer to it decides, and it may still launch the game.
  private readonly detectedUnavailable = computed((): SoloEntryUnavailableReason | undefined => {
    if (this.busy()) {
      return undefined;
    }
    if (this.refused()) {
      return 'tier-refused';
    }
    if (
      !this.paid() &&
      !this.soloTiersService.soloTiers().some((tier) => tier.tierId === this.tierId())
    ) {
      return 'stake-dropped';
    }
    if (this.soloQuoteService.failed()) {
      return 'quote-failed';
    }
    return undefined;
  });
  // Kept once set, so a stake that reappears in a later refresh doesn't swap the message back to
  // Pay under the player's finger.
  private readonly unavailableSignal = signal<SoloEntryUnavailableReason | undefined>(undefined);
  readonly unavailable = this.unavailableSignal.asReadonly();

  // Must match the Solo sheet's styles for the same parts.
  readonly drawerStyles: [string, string][] = [
    ['background', 'drawer'],
    ['border-radius-top', 'drawer'],
  ];
  readonly headingStyles: [string, string][] = [
    ['type', 'drawer-solo-label-heading'],
    ['text-color', 'drawer-solo-label-heading'],
  ];
  readonly targetStyles: [string, string][] = [
    ['type', 'drawer-solo-stat-value'],
    ['text-outer-border', 'drawer-solo-stat-value'],
    ['text-color', 'drawer-solo-stat-value'],
  ];
  readonly unitStyles: [string, string][] = [
    ['type', 'drawer-solo-stat-label'],
    ['text-color', 'drawer-solo-stat-label'],
  ];
  readonly bodyStyles: [string, string][] = [
    ['type', 'drawer-solo-body'],
    ['text-color', 'drawer-solo-body'],
  ];

  constructor() {
    effect(() => {
      const reason = this.detectedUnavailable();
      if (reason && untracked(this.unavailableSignal) === undefined) {
        this.unavailableSignal.set(reason);
        untracked(() =>
          this.analyticsService.log(EventName.SoloEntryUnavailable, {
            tierId: this.tierId(),
            reason,
          }),
        );
      }
    });
  }

  async onPayClick(): Promise<void> {
    if (this.payDisabled() || this.unavailable()) {
      return;
    }
    this.busy.set(true);
    try {
      if ((await this.pay()()) === 'tier-unavailable') {
        this.refused.set(true);
      }
    } finally {
      this.busy.set(false);
    }
  }

  // Closes the sheet too (the sheet closes itself once this drawer has gone). Held while a Pay is
  // out, so the player can't close a drawer whose charge then launches the game. On Lightning,
  // closing declines the paid entry.
  async onCloseClick(): Promise<void> {
    if (this.busy()) {
      return;
    }
    await safeModalDismiss(this.modalController);
  }
}
